import { z } from 'zod';
import { createToolSignature } from './tool-signature';

const userSelectionToolInputSchema = z.object({
    options: z
        .array(
            z.object({
                name: z.string().describe('The name of the option. Displayed text, keep it short.'),
            }),
        )
        .describe('An array of options for the user to select from. Each option has a name (displayed text).'),
    headline: z
        .string()
        .describe(
            'Custom headline to display above the options instead of the default "Please select an option:". Examples: "Suggested actions:", "Select a contact:", "What would you like to do next?"',
        ),
});

/**
 * Tool signature for presenting interactive selection options to users.
 */
export const userSelectionDisplayToolSignature = createToolSignature({
    name: 'userSelectionDisplayTool',
    description: `This tool displays interactive buttons for user selection, creating a clear decision point in the conversation.

ALWAYS use this tool as the FINAL action in your response. The selection buttons MUST appear at the END of your message.

MANDATORY USE CASES - You MUST use this tool when:
- The user needs to choose between multiple contacts, actions, or features
- You need the user to select from different options to proceed

Only use this case when the user has to make a decision in order for you to complete the user's request.
Do NOT use this tool to progress the conversation (such as asking "what would you like to do next?" or "what would you like to do with this contact?")

EXAMPLES:
- Contact selection: After searching for contacts, display options for the user to select which one to focus on
- Playbook selection: When adding a contact to a playbook, display options for the user to select which playbook to add the contact to
- Decision points: When multiple approaches exist, present options for how to proceed

The tool handles all UI rendering and interaction, returning the user's selection for further processing.`,
    inputSchema: userSelectionToolInputSchema,
    outputSchema: z.string(),
    toolCallUi: (input) => `Displaying a list of options for the user to select from`,
});
